/**
 * Deterministic avatar fallback shared by every surface that renders a person
 * without a profile picture.
 *
 * Two rules, one place: the letter and tone follow the username whenever it is
 * available. Non-person entities, such as groups and AI agents, may still use
 * their name when they have no username.
 * Surfaces keep their own size and shape — they only borrow the letter and the
 * `data-avatar-tone` attribute, whose colors live in styles/tokens.css.
 */

/** Tones declared as --color-avatar-tone-N-* in styles/tokens.css. */
export const AVATAR_TONE_COUNT = 8;

export interface AvatarFallbackInput {
  /**
   * Stable identity fallback for the color: profileUserGuid, userId or
   * conversation id. Never a list index or anything that changes per render.
   */
  seed?: string | number | null;
  /** Fallback letter source for non-person entities without a username. */
  name?: string | null;
  /** Preferred letter and canonical tone source. */
  username?: string | null;
}

export interface AvatarFallback {
  /** Single uppercase letter, or '' when nothing identifies the person. */
  initial: string;
  /** 1-based tone index for the `data-avatar-tone` attribute. */
  tone: number;
}

/**
 * First letter or digit of a name. Leading punctuation and emoji are skipped
 * so a name like "*Sam" still shows an S, and any script is accepted.
 */
const firstLetter = (value: string | null | undefined): string => {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return '';
  const match = trimmed.match(/[\p{L}\p{N}]/u);
  return (match?.[0] ?? Array.from(trimmed)[0] ?? '').toUpperCase();
};

export function avatarInitial(name?: string | null, username?: string | null): string {
  return firstLetter(username) || firstLetter(name);
}

export function avatarTone(seed?: string | number | null): number {
  const key = String(seed ?? '').trim().toLowerCase();
  if (!key) return 1;

  // FNV-1a: stable across reloads and spreads sequential ids across the palette,
  // which a plain length or char-sum hash does not.
  let hash = 0x811c9dc5;
  for (let index = 0; index < key.length; index += 1) {
    // eslint-disable-next-line no-bitwise
    hash ^= key.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }

  return (Math.abs(hash) % AVATAR_TONE_COUNT) + 1;
}

export function avatarFallback({ seed, name, username }: AvatarFallbackInput): AvatarFallback {
  return {
    initial: avatarInitial(name, username),
    // Different endpoints may expose different IDs for the same member. Their
    // normalized username is the common identity available across surfaces.
    tone: avatarTone(username ?? seed ?? name),
  };
}
